import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  CommunityGroup,
  GroupListResponse,
  CreateGroupRequest,
} from "@/api/communityGroups/types";
import * as api from "@/api/communityGroups/communityGroupsApi";

interface GroupsListState {
  groups: CommunityGroup[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  loading: boolean;
  error: string | null;
  searchQuery: string;
  filter: "all" | "my-groups";
}

const initialState: GroupsListState = {
  groups: [],
  total: 0,
  page: 1,
  limit: 12,
  totalPages: 0,
  loading: false,
  error: null,
  searchQuery: "",
  filter: "all",
};

export const fetchGroups = createAsyncThunk(
  "groupsList/fetchGroups",
  async (
    params: {
      page?: number;
      limit?: number;
      search?: string;
      myGroups?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.fetchGroups(params);
      return response;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch groups";
      return rejectWithValue(errorMessage);
    }
  },
);

export const createGroup = createAsyncThunk(
  "groupsList/createGroup",
  async (data: CreateGroupRequest, { rejectWithValue }) => {
    try {
      const response = await api.createGroup(data);
      return response;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to create group";
      return rejectWithValue(errorMessage);
    }
  },
);

export const joinGroup = createAsyncThunk(
  "groupsList/joinGroup",
  async (groupUuid: string, { rejectWithValue }) => {
    try {
      await api.joinGroup(groupUuid);
      return groupUuid;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to join group";
      return rejectWithValue(errorMessage);
    }
  },
);

export const leaveGroup = createAsyncThunk(
  "groupsList/leaveGroup",
  async (groupUuid: string, { rejectWithValue }) => {
    try {
      await api.leaveGroup(groupUuid);
      return groupUuid;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to leave group";
      return rejectWithValue(errorMessage);
    }
  },
);

const groupsListSlice = createSlice({
  name: "groupsList",
  initialState,
  reducers: {
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setFilter: (state, action: PayloadAction<"all" | "my-groups">) => {
      state.filter = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch groups
      .addCase(fetchGroups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchGroups.fulfilled,
        (state, action: PayloadAction<GroupListResponse>) => {
          state.loading = false;
          state.groups = action.payload.groups;
          state.total = action.payload.total;
          state.page = action.payload.page;
          state.limit = action.payload.limit;
          state.totalPages = action.payload.totalPages;
          // Backend may not set isMember field, so derive it from membershipStatus/membershipRole
          state.groups.forEach((group) => {
            if (group.isMember === undefined) {
              group.isMember =
                group.membershipStatus === "active" ||
                group.membershipRole !== null;
            }
          });
        },
      )
      .addCase(fetchGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create group
      .addCase(createGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createGroup.fulfilled,
        (state, action: PayloadAction<CommunityGroup>) => {
          state.loading = false;
          // Ensure groups is always an array before spreading
          const currentGroups = Array.isArray(state.groups) ? state.groups : [];
          state.groups = [action.payload, ...currentGroups];
          state.total += 1;
        },
      )
      .addCase(createGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Join group
      .addCase(joinGroup.fulfilled, (state, action: PayloadAction<string>) => {
        // Ensure groups is always an array
        if (!Array.isArray(state.groups)) {
          state.groups = [];
          return;
        }
        const group = state.groups.find((g) => g.uuid === action.payload);
        if (group) {
          group.isMember = true;
          group.membershipStatus = "active";
          group.memberCount += 1;
        }
      })
      // Leave group
      .addCase(leaveGroup.fulfilled, (state, action: PayloadAction<string>) => {
        // Ensure groups is always an array
        if (!Array.isArray(state.groups)) {
          state.groups = [];
          return;
        }
        const group = state.groups.find((g) => g.uuid === action.payload);
        if (group) {
          group.isMember = false;
          group.membershipStatus = null;
          group.membershipRole = null;
          group.memberCount -= 1;
        }
      });
  },
});

export const { setSearchQuery, setFilter, setPage, clearError } =
  groupsListSlice.actions;
export default groupsListSlice.reducer;
